import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { AppRole } from "@/service/AppRoleService";

// Base selectors
const selectAuth = (state: RootState) => state.auth;
const selectUser = (state: RootState) => state.user;

// Authentication state
export const selectIsAuthenticated = createSelector(
  selectAuth,
  (auth) => auth.isAuthenticated,
);

export const selectAccessToken = createSelector(
  selectAuth,
  (auth) => auth.token,
);

export const selectAuthUser = createSelector(selectAuth, (auth) => auth.user);

export const selectAuthError = createSelector(selectAuth, (auth) => auth.error);

export const selectTokenExpiresAt = createSelector(
  selectAuth,
  (auth) => auth.expiresAt,
);

// User information
export const selectUserInfo = createSelector(
  selectUser,
  (user) => user.userInfo,
);

export const selectUserDisplayName = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.displayName,
);

export const selectUserLoading = createSelector(
  selectUser,
  (user) => user.isLoading,
);

export const selectUserError = createSelector(selectUser, (user) => user.error);

// Role management - now sourced from userSlice
export const selectAppRoles = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.appRoles || [],
);

export const selectIsAdmin = createSelector(selectAppRoles, (roles) =>
  roles.includes(AppRole.SCRM_APP_ADMIN),
);

export const selectIsReadOnlyUser = createSelector(selectAppRoles, (roles) =>
  roles.includes(AppRole.SCRM_APP_READ_ONLY),
);

// Role utilities
export const selectHasRole = (role: AppRole) =>
  createSelector(selectAppRoles, (roles) => roles.includes(role));

export const selectHasAnyRole = (roles: AppRole[]) =>
  createSelector(selectAppRoles, (userRoles) =>
    roles.some((role) => userRoles.includes(role)),
  );

export const selectHasAllRoles = (roles: AppRole[]) =>
  createSelector(selectAppRoles, (userRoles) =>
    roles.every((role) => userRoles.includes(role)),
  );

// Combined selectors for convenience
export const selectAuthAndUserState = createSelector(
  [selectAuth, selectUserInfo],
  (auth, userInfo) => ({
    isAuthenticated: auth.isAuthenticated,
    user: auth.user,
    userInfo,
    appRoles: userInfo?.appRoles || [],
    isLoading: auth.token === null && userInfo === null,
  }),
);
